<template>
  <div class="container-fluid aip-screen">
    <page-header
      :page="page"
      has-return
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    >
      <template v-slot:customActions>
        <!--
          En modo solo-lectura (llegó por "Ver") no se guarda nada -- se muestra en su lugar un
          único botón para pasar a edición ("crear una versión nueva a partir de esta"), sin
          tener que volver al historial y hacer clic en otro lado.
        -->
        <ActionButton
          v-if="isViewMode"
          class="mb-1 aip-btn-publish"
          text="Crear versión a partir de esta"
          variant="success"
          i="fas fa-code-branch"
          @click="enableEditingFromView"
        />
        <template v-else>
          <ActionButton
            text="Guardar Borrador"
            class="mb-1 aip-btn-draft"
            variant="outline-success"
            i="fas fa-save"
            @click="saveDraft(false)"
          />
          <!--
            "Guardar y Publicar" solo se ofrece cuando reemplazar la versión activa es de bajo
            riesgo: "Nueva instrucción" desde cero (todavía no hay nada Publicado para esa clave)
            o cuando se parte de una versión que YA estaba en Borrador (nunca estuvo viva, así
            que publicarla de una vez no reemplaza nada sin revisión). Si se parte de una versión
            Publicada o Archivada, se oculta a propósito -- hay que guardar como Borrador primero
            y publicar aparte desde el historial, como paso extra de revisión antes de reemplazar
            lo que el Orquestador está usando ahora mismo.
          -->
          <ActionButton
            v-if="canPublicarDirectamente"
            class="mb-1 ml-1 aip-btn-publish"
            text="Guardar y Publicar"
            variant="success"
            i="fas fa-cloud-upload-alt"
            @click="saveDraft(true)"
          />
        </template>
      </template>
    </page-header>

    <!--
      Encabezado visual adicional (burbuja + título), inspirado en el mockup entregado. No
      reemplaza el PageHeader real (que sigue manejando el breadcrumb y el botón "Regresar"),
      solo agrega contexto encima del formulario. El Estatus se muestra dentro del panel de
      metadata de abajo, no aquí (para no duplicarlo).
      - NO se agrega un botón "Comparar versiones": esa función no existe en el backend.
    -->
    <div class="aip-hero d-flex align-items-center mb-3">
      <div class="aip-hero-icon">
        <i class="fas fa-comment-dots"></i>
      </div>
      <div class="ml-3">
        <div class="aip-hero-title">
          {{ heroTitle }}
        </div>
        <div class="aip-hero-subtitle" v-if="sourceMeta">
          A partir de <strong>{{ sourceMeta.technicalKey }}</strong> — versión {{ sourceMeta.version }}
        </div>
      </div>
    </div>

    <!--
      Panel de metadata de la versión de origen -- todos los datos son reales (vienen de
      GetHistoryAsync/ToResponseAsync). "Creado por"/"Publicado por" ahora sí muestran nombre
      real: el backend resuelve CreatedById/UpdatedById a un nombre vía
      IUserProfileEngine.GetUserNameByIdAsync (mismo mecanismo real ya usado en
      UmesrEngine/PeiEngine/ProviderAppointmentEngine contra EDUESP_User.FullName) -- ver
      AIPromptConfigurationController.ToResponseAsync.

      "Publicado por" solo se muestra cuando el Estatus actual de esta fila es Publicado: la
      columna UpdatedById/UpdatedDate es genérica ("última vez que se tocó esta fila", la pisan
      tanto PublishAsync como ArchiveAsync) -- si la fila está Archivada, quien aparece ahí es
      quien la archivó, no quien la publicó, así que etiquetarlo "Publicado por" en ese caso
      sería incorrecto. Para Archivado se muestra "Archivado por" en su lugar.
    -->
    <div v-if="sourceMeta" class="aip-meta-card mb-3">
      <div class="aip-meta-item">
        <i class="fas fa-hashtag"></i>
        <span class="aip-meta-label">Prompt ID</span>
        <span class="aip-meta-value">{{ promptIdFormateado }}</span>
      </div>
      <div class="aip-meta-item">
        <i class="fas fa-key"></i>
        <span class="aip-meta-label">Clave técnica</span>
        <span class="aip-meta-value">{{ sourceMeta.technicalKey }}</span>
      </div>
      <div class="aip-meta-item">
        <i class="fas fa-code-branch"></i>
        <span class="aip-meta-label">Versión de origen</span>
        <span class="aip-meta-value">{{ sourceMeta.version }}</span>
      </div>
      <div class="aip-meta-item">
        <i class="fas fa-circle-notch"></i>
        <span class="aip-meta-label">Estatus</span>
        <span class="aip-status-badge" :class="estadoBadgeClass">{{ sourceMeta.status }}</span>
      </div>
      <div class="aip-meta-item">
        <i class="fas fa-user"></i>
        <span class="aip-meta-label">Creado por</span>
        <span class="aip-meta-value">{{ sourceMeta.createdByName || 'No disponible' }}</span>
      </div>
      <div class="aip-meta-item">
        <i class="fas fa-calendar-plus"></i>
        <span class="aip-meta-label">Creado</span>
        <span class="aip-meta-value">{{ formatDate(sourceMeta.createdDate) }}</span>
      </div>
      <div class="aip-meta-item" v-if="ultimaModificacionPorLabel">
        <i class="fas fa-user-check"></i>
        <span class="aip-meta-label">{{ ultimaModificacionPorLabel }}</span>
        <span class="aip-meta-value">{{ sourceMeta.updatedByName || 'No disponible' }}</span>
      </div>
      <div class="aip-meta-item" v-if="sourceMeta.updatedDate">
        <i class="fas fa-calendar-check"></i>
        <span class="aip-meta-label">Última modificación</span>
        <span class="aip-meta-value">{{ formatDate(sourceMeta.updatedDate) }}</span>
      </div>
    </div>

    <!--
      Mensaje de Borrador: visible siempre que se vaya a guardar algo en esta pantalla. Se oculta
      en modo solo-lectura (isViewMode): ahí no se va a guardar nada todavía, así que hablar de
      "borrador"/"versión" no aplica. El texto tiene 3 variantes reales (25 ago 2026, ver
      isEditingExistingDraft):
      - Sin fila de origen ("Nueva instrucción"): se crea una fila nueva, v1.
      - Editando una fila que YA está en Borrador: se actualiza esa MISMA fila en sitio
        (updateDraft) -- ya NO se crea una versión nueva por cada guardado (antes de este fix,
        CreateDraftAsync insertaba una fila nueva incluso re-guardando el mismo borrador que se
        acababa de crear -- Astianax lo reportó: "un registro en borrador ya no se puede
        editar").
      - Editando a partir de una fila Publicada/Archivada ("Nueva versión desde aquí"): sí se
        crea una fila nueva (rama nueva) -- esas deben permanecer inmutables.
    -->
    <div v-if="!isViewMode" class="aip-draft-banner mb-3">
      <i class="fas fa-info-circle mr-2"></i>
      <template v-if="isEditingExistingDraft">
        Al guardar, se actualiza <strong>este mismo borrador</strong> (no se crea una versión
        nueva). No estará disponible para el Servicio Orquestador del Asistente de IA hasta que
        lo publiques (con "Guardar y Publicar", o más adelante desde el historial de versiones).
      </template>
      <template v-else-if="canPublicarDirectamente">
        Al guardar, esta instrucción se crea como una <strong>nueva versión en estado Borrador</strong>.
        No estará disponible para el Servicio Orquestador del Asistente de IA hasta que la
        publiques (con "Guardar y Publicar", o más adelante desde el historial de versiones).
      </template>
      <template v-else>
        Al guardar, esta instrucción se crea como una <strong>nueva versión en estado Borrador</strong>.
        Como partes de una versión {{ sourceMeta.status }}, aquí solo se ofrece "Guardar
        Borrador" -- para publicarla y reemplazar la versión actualmente activa, hazlo aparte
        desde el historial de versiones, como paso extra de revisión.
      </template>
    </div>

    <ValidationObserver ref="createEditForm">
      <CreateEditForm
        ref="formInformation"
        v-model="model"
        :is-edit-mode="isEditMode"
        :read-only="isViewMode"
      />
    </ValidationObserver>

    <!--
      Modo de Prueba (25 ago 2026): corre EXACTAMENTE lo que está escrito arriba en ese momento
      (guardado o no) contra un PEI real, sin crear una versión nueva ni publicar nada. Se muestra
      siempre que ya exista un ancla guardada (sourceMeta.aiPromptConfigurationId) -- CODE_
      AIPromptConfiguration es inmutable y LOG_AiGeneration exige un Id real ya guardado (ver
      PeiAITestPromptRequest.AIPromptConfigurationId), así que no se puede probar antes de
      guardar al menos un borrador. Vive aparte del formulario (no dentro de CreateEditForm) para
      que el formulario se quede editable en todo momento, tal como se pidió.
    -->
    <div class="aip-card aip-testmode-card mt-3">
      <div class="aip-card-header" @click="showTestMode = !showTestMode">
        <div class="d-flex align-items-center">
          <i class="fas fa-flask mr-2"></i>
          <span class="aip-card-title">Modo de Prueba</span>
        </div>
        <i :class="showTestMode ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
      </div>

      <div v-show="showTestMode" class="aip-card-body">
        <p class="aip-testmode-hint">
          Corre lo que está escrito arriba (guardado o no) contra un PEI real, sin crear una
          versión nueva ni publicar nada. Cada prueba queda registrada como evidencia, para poder
          mostrarla antes de publicar un cambio.
        </p>

        <div v-if="!canTest" class="aip-testmode-locked">
          <i class="fas fa-lock mr-2"></i>
          Guarda esta instrucción como Borrador al menos una vez (botón "Guardar Borrador" arriba)
          para poder probarla.
        </div>

        <template v-else>
          <div class="row align-items-end">
            <div class="col-md-5">
              <label class="aip-field-label">Buscar estudiante por SIE</label>
              <input
                v-model="testSieId"
                type="text"
                class="form-control"
                placeholder="Número de SIE"
                @keypress.enter="buscarEstudianteParaPrueba"
              />
            </div>
            <div class="col-md-3">
              <button
                type="button"
                class="btn aip-btn-search"
                :disabled="testSearchLoading"
                @click="buscarEstudianteParaPrueba"
              >
                <i class="fas fa-search mr-1"></i>
                {{ testSearchLoading ? 'Buscando...' : 'Buscar' }}
              </button>
            </div>
          </div>

          <div v-if="testSearchError" class="aip-testmode-error mt-2">{{ testSearchError }}</div>
          <div v-if="testPeiLoadError" class="aip-testmode-error mt-2">{{ testPeiLoadError }}</div>

          <!-- Se muestra en cuanto se encuentra el estudiante (paso 1), aunque el PEI (paso 2)
               todavía no haya resuelto o haya fallado -- así el usuario ve con claridad que el
               estudiante SÍ se encontró y que el problema, si lo hay, es del PEI. -->
          <div v-if="testStudent" class="aip-testmode-student mt-2">
            <i class="fas fa-user-check mr-2"></i>
            <strong>{{ testStudent.fullName }}</strong>
            <span v-if="testPeiId" class="ml-2 aip-testmode-pei">PEI {{ testPeiId }}</span>
          </div>

          <div class="mt-3">
            <label class="aip-field-label">
              Nota de fuente faltante
              <span class="aip-field-optional">
                (opcional, no se guarda en el sistema -- solo para copiarla y avisarle al equipo
                de desarrollo)
              </span>
            </label>
            <textarea
              v-model="testMissingSourceNote"
              class="form-control"
              rows="2"
              placeholder="Ej. Necesitaría el historial de terapias del estudiante, que hoy no está disponible como fuente."
            ></textarea>
          </div>

          <div class="mt-3 d-flex">
            <button
              type="button"
              class="btn aip-btn-run"
              :disabled="!testPeiId || testRunning"
              @click="ejecutarPrueba"
            >
              <i class="fas fa-play mr-1"></i>
              {{ testRunning ? 'Probando...' : 'Probar' }}
            </button>
            <button type="button" class="btn aip-btn-clear ml-2" @click="limpiarPrueba">
              Limpiar
            </button>
          </div>

          <div v-if="testError" class="aip-testmode-error mt-3">{{ testError }}</div>

          <div v-if="testResult" class="aip-testmode-result mt-3">
            <div v-if="testResult.isBlocked" class="aip-testmode-blocked">
              <i class="fas fa-exclamation-triangle mr-2"></i>
              {{ testResult.blockedReason }}
            </div>

            <template v-else>
              <div class="aip-testmode-result-block">
                <div class="aip-testmode-result-label">Respuesta del modelo</div>
                <pre class="aip-testmode-pre">{{ testResult.suggestion }}</pre>
              </div>

              <div class="aip-testmode-result-block" v-if="testResult.finalPrompt">
                <div class="aip-testmode-result-label">Prompt final enviado</div>
                <pre class="aip-testmode-pre">{{ testResult.finalPrompt }}</pre>
              </div>
            </template>

            <div
              class="aip-testmode-result-block"
              v-if="testResult.detectedContext && testResult.detectedContext.length"
            >
              <div class="aip-testmode-result-label">Contexto detectado</div>
              <ul class="aip-testmode-context-list">
                <li v-for="fuente in testResult.detectedContext" :key="fuente.sourceKey">
                  <strong>{{ fuente.sourceKey }}:</strong>
                  <span v-if="fuente.found">{{ fuente.value }}</span>
                  <span v-else class="aip-testmode-context-missing">{{ fuente.note || 'Sin dato' }}</span>
                </li>
              </ul>
            </div>

            <div class="aip-testmode-log-id" v-if="testResult.aiGenerationLogId">
              Registrado en auditoría como prueba (LOG_AiGeneration #{{ testResult.aiGenerationLogId }})
            </div>
          </div>
        </template>
      </div>
    </div>

    <!--
      Insights de Feedback: métricas reales de uso de ESTA versión guardada (LOG_AiGeneration/
      LOG_AiGenerationFeedback/LOG_AiGenerationAppliedField), pensadas para ayudar a afinar el
      prompt según cómo se está usando en la práctica -- sin tener que consultar la base de datos
      directamente. Mismo requisito que Modo de Prueba (canTest): no aplica a "Nueva instrucción"
      sin guardar todavía.
    -->
    <div v-if="canTest" class="aip-card aip-testmode-card mt-3">
      <div class="aip-card-header" @click="showInsights = !showInsights">
        <div class="d-flex align-items-center">
          <i class="fas fa-chart-line mr-2"></i>
          <span class="aip-card-title">Insights de Feedback</span>
        </div>
        <i :class="showInsights ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
      </div>

      <div v-show="showInsights" class="aip-card-body">
        <p class="aip-testmode-hint">
          Uso real de esta versión: cuántas veces se generó, si el especialista la usó y si le
          fue útil. Ayuda a decidir qué ajustar en el prompt antes de publicar un cambio.
        </p>

        <div v-if="insightsLoading" class="aip-testmode-hint">Cargando insights...</div>
        <div v-else-if="insightsError" class="aip-testmode-error">{{ insightsError }}</div>

        <template v-else-if="insights">
          <div v-if="insights.totalGenerations === 0" class="aip-testmode-locked">
            <i class="fas fa-info-circle mr-2"></i>
            Esta versión todavía no tiene ninguna generación real registrada (sin contar Modo de
            Prueba).
          </div>

          <template v-else>
            <div class="aip-insights-stats">
              <div class="aip-insights-stat">
                <div class="aip-insights-stat-value">{{ insights.totalGenerations }}</div>
                <div class="aip-insights-stat-label">Generaciones reales</div>
              </div>
              <div class="aip-insights-stat">
                <div class="aip-insights-stat-value">{{ insights.usedCount }}</div>
                <div class="aip-insights-stat-label">Usadas</div>
              </div>
              <div class="aip-insights-stat aip-insights-stat--positive">
                <div class="aip-insights-stat-value">{{ insights.positiveFeedbackCount }}</div>
                <div class="aip-insights-stat-label">👍 Útil</div>
              </div>
              <div class="aip-insights-stat aip-insights-stat--negative">
                <div class="aip-insights-stat-value">{{ insights.negativeFeedbackCount }}</div>
                <div class="aip-insights-stat-label">👎 No útil</div>
              </div>
              <div class="aip-insights-stat">
                <div class="aip-insights-stat-value">{{ insights.noFeedbackCount }}</div>
                <div class="aip-insights-stat-label">Sin feedback</div>
              </div>
              <div
                class="aip-insights-stat"
                v-if="insights.errorCount || insights.incompleteContextCount"
              >
                <div class="aip-insights-stat-value">
                  {{ insights.errorCount + insights.incompleteContextCount }}
                </div>
                <div class="aip-insights-stat-label">Bloqueadas/con error</div>
              </div>
            </div>

            <p class="aip-testmode-hint mt-2" v-if="!insights.tracksAppliedField">
              Esta sección todavía no mide si la sugerencia quedó aplicada en el campo del PEI --
              "Usadas" viene solo del botón "Usar Esta Versión" cuando existe, no de una
              verificación automática.
            </p>
            <p class="aip-testmode-hint mt-2" v-else>
              {{ insights.appliedFieldCount }} de {{ insights.totalGenerations }} generaciones
              quedaron efectivamente aplicadas en el campo del PEI.
            </p>

            <template v-if="insights.recentNegativeFeedback && insights.recentNegativeFeedback.length">
              <div class="aip-testmode-result-label mt-3">Feedback negativo reciente (👎)</div>
              <div
                v-for="muestra in insights.recentNegativeFeedback"
                :key="muestra.aiGenerationLogId"
                class="aip-insights-sample mt-2"
              >
                <div class="aip-testmode-log-id">
                  PEI {{ muestra.peiId }} — {{ formatDate(muestra.createdDate) }} —
                  LOG_AiGeneration #{{ muestra.aiGenerationLogId }}
                </div>
                <div class="aip-testmode-result-block" v-if="muestra.finalPrompt">
                  <div class="aip-testmode-result-label">Prompt final enviado</div>
                  <pre class="aip-testmode-pre">{{ muestra.finalPrompt }}</pre>
                </div>
                <div class="aip-testmode-result-block" v-if="muestra.responsePayload">
                  <div class="aip-testmode-result-label">Respuesta del modelo</div>
                  <pre class="aip-testmode-pre">{{ muestra.responsePayload }}</pre>
                </div>
              </div>
            </template>
          </template>
        </template>
      </div>
    </div>

    <!-- Fundamento Normativo: respuestas del RAG guardadas para esta versión (se calculan al publicar). -->
    <div v-if="canTest" class="aip-card aip-testmode-card mt-3">
      <div class="aip-card-header" @click="showRegulation = !showRegulation">
        <div class="d-flex align-items-center">
          <i class="fas fa-balance-scale mr-2"></i>
          <span class="aip-card-title">Fundamento Normativo</span>
        </div>
        <i :class="showRegulation ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
      </div>

      <div v-show="showRegulation" class="aip-card-body">
        <p class="aip-testmode-hint">
          Respuestas del Asistente Virtual (RAG) a las preguntas normativas de esta versión. Se
          guardan al publicar y se reutilizan en cada generación, sin volver a consultar el RAG.
          Actualícelas cuando cambien los documentos normativos indexados.
        </p>

        <div v-if="regulationLoading" class="aip-testmode-hint">Cargando Fundamento Normativo...</div>
        <div v-else-if="regulationError" class="aip-testmode-error">{{ regulationError }}</div>
        <div v-else-if="!regulation.length" class="aip-testmode-locked">
          <i class="fas fa-info-circle mr-2"></i>
          No hay Fundamento Normativo guardado para esta versión. Si tiene preguntas normativas, se
          calcula al publicar o en la primera generación.
        </div>
        <ul v-else class="aip-testmode-context-list">
          <li v-for="item in regulation" :key="item.sortOrder">
            <div class="d-flex align-items-start">
              <span :class="['aip-regulation-status', regulationStatusClass(item)]">
                {{ regulationStatusText(item) }}
              </span>
              <strong class="ml-2">{{ item.question }}</strong>
            </div>
            <div v-if="item.found" class="aip-regulation-value">{{ item.value }}</div>
            <div v-else class="aip-testmode-context-missing aip-regulation-value">{{ item.note }}</div>
          </li>
        </ul>

        <div class="mt-3 d-flex">
          <button
            type="button"
            class="btn aip-btn-run"
            :disabled="regulationRefreshing"
            @click="refreshRegulation"
          >
            <i class="fas fa-sync-alt mr-1"></i>
            {{ regulationRefreshing ? 'Actualizando...' : 'Actualizar Fundamento Normativo' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import { ValidationObserver } from 'vee-validate';
import CreateEditForm from './components/CreateEditForm';
import alertMixin from '@/mixin/alert.mixin';
import {
  getHistory,
  createDraft,
  updateDraft,
  publishConfiguration,
  getInsights,
  getRegulation,
  refreshRegulation,
} from '@/modules/operation-ai-prompt-management/api';
import { http } from '@/core/api/';
import studentService from '@/services/student';
import { getCurrentAcademicYear } from '@/services/api/tapRequest.api.js';
import { testPrompt } from '@/services/pei/aiAssistant';

const MAIN_ROUTE_NAME = 'aiPromptConfig';

const emptyModel = () => ({
  module: null,
  sectionArea: null,
  displayName: '',
  referenceCurriculum: null,
  systemMessageText: '',
  promptText: '',
  sources: [],
  // Por fuente documental (ver CODE_AIPromptSource.DocumentBackedFlag), configuración de
  // lectura de sus documentos adjuntos -- llave = Name de la fuente (p.ej. 'PruebasAcademicas'),
  // valor = { includeDocumentContent, maxDocuments, maxDocumentChars }. Ver
  // AIPromptConfigurationSourceOptionsDTO / AIPromptConfigurationContentDTO.DocumentSourceOptions.
  documentSourceOptions: {},
  maxTokens: null,
  temperature: null,
  topP: null,
  responseJsonSchema: '',
  responseJsonSchemaName: '',
  enumSourcesJson: '',
  // Caja de "Buenas prácticas" del panel del Asistente: título/enlace son columnas de
  // CODE_AIPromptConfiguration y cada punto una fila de AIPromptConfigurationBestPractice.
  // Es contenido informativo para el especialista -- no entra al prompt.
  bestPracticesTitle: '',
  bestPracticesLinkText: '',
  bestPracticesLinkUrl: '',
  bestPractices: [],
});

export default {
  name: 'AiPromptConfigCreateEdit',
  mixins: [alertMixin],
  components: {
    PageHeader,
    ActionButton,
    ValidationObserver,
    CreateEditForm,
  },
  data() {
    return {
      model: emptyModel(),
      // Metadata real de la versión de origen (solo cuando se está creando una nueva versión a
      // partir de una existente) -- technicalKey/version/estado, tal como los devuelve
      // AIPromptConfigurationController.ToResponseAsync. Null en "Nueva instrucción" desde cero.
      sourceMeta: null,

      // ---- Modo de Prueba (25 ago 2026) ----
      showTestMode: false,
      testSieId: '',
      testSearchLoading: false,
      testSearchError: '',
      testStudent: null,
      testPeiId: '',
      testPeiLoadError: '',
      // Nota de fuente faltante: solo vive en memoria de este componente -- a propósito NUNCA se
      // envía al backend ni se guarda en BD (así se confirmó con el usuario). Sirve para que el
      // especialista la copie y se la pase aparte al equipo de desarrollo.
      testMissingSourceNote: '',
      testRunning: false,
      testResult: null,
      testError: '',

      // ---- Insights de Feedback ----
      showInsights: false,
      insights: null,
      insightsLoading: false,
      insightsError: '',

      // ---- Fundamento Normativo ----
      showRegulation: false,
      regulation: [],
      regulationLoading: false,
      regulationError: '',
      regulationRefreshing: false,
    };
  },
  computed: {
    // "Edición" aquí siempre significa "nueva versión Borrador a partir de una existente" --
    // CODE_AIPromptConfiguration nunca se actualiza in-place (ver invariante de
    // AIPromptConfigurationEngine: Borrador/Publicado/Archivado, versiones inmutables).
    isEditMode() {
      return Boolean(this.$route.query.sourceId);
    },
    // "Ver" (desde el historial) navega a esta misma pantalla con ?mode=view -- solo lectura,
    // sin botones de Guardar/Publicar. Distinto de isEditMode (que solo indica "hay un sourceId",
    // sin importar si es para ver o para crear una versión nueva a partir de él).
    isViewMode() {
      return this.$route.query.mode === 'view';
    },
    heroTitle() {
      if (this.isViewMode) {
        return 'Ver versión de instrucción';
      }
      return this.isEditMode ? 'Nueva versión de instrucción' : 'Nueva instrucción de IA';
    },
    page() {
      let crumb = 'Nueva instrucción';
      if (this.isViewMode) {
        crumb = 'Ver versión';
      } else if (this.isEditMode) {
        crumb = 'Nueva versión';
      }
      return {
        icon: 'fas fa-robot',
        title: 'Consola de Instrucciones IA',
        routes: ['Inicio', 'Operaciones', 'Consola de Instrucciones IA', crumb],
      };
    },
    // Ver comentario junto al botón "Guardar y Publicar" en el template. true cuando no hay
    // versión de origen (Nueva instrucción) o cuando el origen está en Borrador; false cuando
    // el origen está Publicado o Archivado (ahí solo se ofrece Guardar Borrador).
    canPublicarDirectamente() {
      if (!this.sourceMeta) {
        return true;
      }
      return this.sourceMeta.status === 'Borrador';
    },
    // 25 ago 2026: a diferencia de canPublicarDirectamente (que también es true para "Nueva
    // instrucción", sin sourceMeta todavía), este SOLO es true cuando ya existe una fila de
    // origen y esa fila sigue en Borrador -- es la condición real que decide si "Guardar"
    // actualiza esa misma fila en sitio (updateDraft) o crea una fila nueva (createDraft). Se
    // reusa tanto en saveDraft() como en el mensaje del banner de abajo para que ambos queden
    // sincronizados con un solo criterio.
    isEditingExistingDraft() {
      return Boolean(this.sourceMeta && this.sourceMeta.aiPromptConfigurationId) &&
        this.sourceMeta.status === 'Borrador';
    },
    estadoBadgeClass() {
      const status = this.sourceMeta && this.sourceMeta.status;
      if (status === 'Publicado') return 'aip-badge-published';
      if (status === 'Borrador') return 'aip-badge-draft';
      if (status === 'Archivado') return 'aip-badge-archived';
      return 'aip-badge-default';
    },
    promptIdFormateado() {
      if (!this.sourceMeta || !this.sourceMeta.aiPromptConfigurationId) {
        return '';
      }
      return `PRM-${String(this.sourceMeta.aiPromptConfigurationId).padStart(4, '0')}`;
    },
    // Etiqueta dinámica para el campo "actualizado por": UpdatedById/UpdatedDate es genérico
    // (lo pisan tanto PublishAsync como ArchiveAsync), así que el rótulo depende del Estatus
    // actual para no decir "Publicado por" en una fila que en realidad fue archivada después.
    // Se muestra SIEMPRE que el Estatus sea Publicado/Archivado -- igual que "Creado por", el
    // valor cae a "No disponible" en el template si updatedByName vino vacío (ej. filas
    // sembradas por script en data/CODE_AIPromptConfiguration.sql que quedaron Publicadas sin
    // pasar nunca por PublishAsync, y por lo tanto sin UpdatedById que resolver) -- antes esto
    // ocultaba la fila entera en vez de mostrar "No disponible", lo cual parecía que la
    // funcionalidad no existía.
    ultimaModificacionPorLabel() {
      if (!this.sourceMeta) {
        return '';
      }
      if (this.sourceMeta.status === 'Publicado') return 'Publicado por';
      if (this.sourceMeta.status === 'Archivado') return 'Archivado por';
      return '';
    },
    // CODE_AIPromptConfiguration es inmutable y LOG_AiGeneration exige un Id ya guardado (ver
    // PeiAITestPromptRequest.AIPromptConfigurationId) -- "Probar" requiere haber guardado al
    // menos un borrador antes, sin importar si isViewMode es true o false.
    canTest() {
      return Boolean(this.sourceMeta && this.sourceMeta.aiPromptConfigurationId);
    },
  },
  methods: {
    async loadSource() {
      const { module: moduleName, sectionArea, sourceId } = this.$route.query;

      this.model = {
        ...emptyModel(),
        module: moduleName,
        sectionArea,
      };

      if (!sourceId) {
        return;
      }

      const history = await getHistory(moduleName, sectionArea);
      const source = history.find(
        (h) => String(h.aiPromptConfigurationId) === String(sourceId)
      );

      if (!source) {
        this.ShowToast('Alerta', 'No se encontró la versión de origen.', 'error');
        return;
      }

      this.model = {
        ...this.model,
        displayName: source.displayName || '',
        referenceCurriculum: source.referenceCurriculum,
        systemMessageText: source.systemMessageText,
        promptText: source.promptText,
        sources: [...(source.sources || [])],
        documentSourceOptions: { ...(source.documentSourceOptions || {}) },
        maxTokens: source.maxTokens,
        temperature: source.temperature,
        topP: source.topP,
        responseJsonSchema: source.responseJsonSchema || '',
        responseJsonSchemaName: source.responseJsonSchemaName || '',
        enumSourcesJson: source.enumSourcesJson || '',
        bestPracticesTitle: source.bestPracticesTitle || '',
        bestPracticesLinkText: source.bestPracticesLinkText || '',
        bestPracticesLinkUrl: source.bestPracticesLinkUrl || '',
        bestPractices: [...(source.bestPractices || [])],
      };

      this.sourceMeta = {
        aiPromptConfigurationId: source.aiPromptConfigurationId,
        technicalKey: source.technicalKey,
        version: source.version,
        status: source.status,
        createdDate: source.createdDate,
        createdByName: source.createdByName,
        updatedDate: source.updatedDate,
        updatedByName: source.updatedByName,
      };
    },
    async saveDraft(publishAfter) {
      const valid = await this.$refs.createEditForm.validate();
      if (!valid) {
        this.ShowToast('Alerta', 'Favor de completar la información solicitada.', 'error');
        return;
      }

      const payload = {
        ...this.model,
        // Los campos JSON avanzados se guardan tal cual como texto -- CreateDraftAsync/
        // UpdateDraftAsync no los validan más allá de que la BD los acepta como VARCHAR(MAX)
        // nullable.
        responseJsonSchema: this.model.responseJsonSchema || null,
        responseJsonSchemaName: this.model.responseJsonSchemaName || null,
        enumSourcesJson: this.model.enumSourcesJson || null,
        bestPracticesTitle: this.model.bestPracticesTitle || null,
        bestPracticesLinkText: this.model.bestPracticesLinkText || null,
        bestPracticesLinkUrl: this.model.bestPracticesLinkUrl || null,
        // Se descartan los puntos vacíos que hayan quedado de un "Agregar punto" sin llenar --
        // el backend igual los filtra, pero así no se manda ruido ni se corre el Orden.
        bestPractices: (this.model.bestPractices || [])
          .map((punto) => (punto || '').trim())
          .filter((punto) => punto.length),
      };

      // 25 ago 2026: si ya se está editando una fila que SIGUE en Borrador (mismo Id, mismo
      // Version), se actualiza EN SITIO (updateDraft) en vez de crear otra fila -- createDraft
      // siempre inserta, así que reusarlo aquí inflaba el historial con una versión nueva en
      // cada guardado, incluso antes de publicar nada (Astianax lo reportó: "un registro en
      // borrador ya no se puede editar"). Solo se sigue creando una fila nueva (createDraft)
      // cuando no hay fila de origen ("Nueva instrucción") o cuando el origen YA está
      // Publicado/Archivado ("Nueva versión desde aquí") -- esas SÍ deben permanecer inmutables.
      // Ver isEditingExistingDraft (computed) y AIPromptConfigurationEngine.UpdateDraftAsync
      // para la validación real de este criterio en el backend (lanza 400 si la fila ya no
      // está en Borrador).
      const editingExistingDraft = this.isEditingExistingDraft;

      try {
        const draft = editingExistingDraft
          ? await updateDraft(this.sourceMeta.aiPromptConfigurationId, payload)
          : await createDraft(payload);

        if (publishAfter) {
          await publishConfiguration(draft.aiPromptConfigurationId);
        }

        let successMessage;
        if (publishAfter) {
          successMessage = editingExistingDraft
            ? 'La instrucción fue actualizada y publicada.'
            : 'La instrucción fue creada y publicada.';
        } else {
          successMessage = editingExistingDraft
            ? 'Los cambios del borrador fueron guardados.'
            : 'La instrucción fue guardada como Borrador.';
        }

        this.ShowToast('Proceso Exitoso.', successMessage, 'success');

        if (publishAfter) {
          // "Guardar y Publicar" sí regresa a la bandeja -- ya no queda nada más que editar
          // aquí, la fila recién publicada se gestiona desde el historial.
          this.goBack();
        } else {
          // "Guardar Borrador" se queda en esta misma pantalla, ya editando el borrador recién
          // creado, en vez de regresar a la bandeja y obligar a volver a buscar y abrir la fila
          // para seguir ajustándola -- así lo pidió el usuario explícitamente.
          this.loadCreatedDraft(draft);
        }
      } catch (e) {
        const message = e?.response?.data || e.message;
        this.ShowToast('Error', message, 'error');
      }
    },
    goBack() {
      this.$router.push({ name: MAIN_ROUTE_NAME });
    },
    // Después de "Guardar Borrador" (nunca de "Guardar y Publicar", ver saveDraft): en vez de
    // regresar a la bandeja, esta pantalla se queda abierta ya editando el borrador recién
    // creado. "draft" es la respuesta real de createDraft (AIPromptConfigurationController.
    // ToResponseAsync), con la misma forma que ya arma loadSource() a partir del historial --
    // no hace falta una segunda llamada al backend para refrescar el panel de metadata.
    loadCreatedDraft(draft) {
      this.sourceMeta = {
        aiPromptConfigurationId: draft.aiPromptConfigurationId,
        technicalKey: draft.technicalKey,
        version: draft.version,
        status: draft.status,
        createdDate: draft.createdDate,
        createdByName: draft.createdByName,
        updatedDate: draft.updatedDate,
        updatedByName: draft.updatedByName,
      };

      // Mantiene la URL en sincronía con la fila que ahora se está editando (sourceId pasa a ser
      // el id del borrador recién creado), sin recargar el componente ni disparar mounted() otra
      // vez -- Vue Router no re-monta al navegar a la misma ruta solo cambiando el query. Se usa
      // replace (no push) porque esto no es una navegación nueva del usuario, es solo corregir la
      // URL para que quede consistente con lo que ya se está viendo/editando.
      const { mode, ...restoQuery } = this.$route.query;
      this.$router.replace({
        name: this.$route.name,
        query: { ...restoQuery, sourceId: String(draft.aiPromptConfigurationId) },
      });

      // No bloqueante -- es solo para refrescar la tarjeta de Insights con el Id recién
      // asignado (que hasta ahora no tenía ninguna generación registrada todavía).
      this.loadInsights().catch(() => {});
      this.loadRegulation().catch(() => {});
    },
    // Pasa de "Ver" (solo lectura) a "crear nueva versión" sin recargar datos -- el modelo y
    // sourceMeta ya están cargados en memoria, solo hace falta quitar mode=view de la ruta para
    // que isViewMode se vuelva false y se habiliten los campos y los botones de Guardar.
    enableEditingFromView() {
      const { mode, ...restoQuery } = this.$route.query;
      this.$router.push({ name: this.$route.name, query: restoQuery });
    },
    // Formateo simple de fecha (sin librería) -- solo para el panel de metadata. Si la fecha
    // viene nula o inválida, se devuelve '' en vez de "Invalid Date".
    formatDate(iso) {
      if (!iso) {
        return '';
      }
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleDateString('es-PR', { year: 'numeric', month: 'short', day: '2-digit' });
    },
    // ---- Modo de Prueba (25 ago 2026) ----
    // Resuelve SIE -> PEI_General.PeiId con los mismos servicios reales que ya usa el resto de
    // la app: studentService.getBySie (igual que components/student/search/Fast.vue) y
    // api/pei/getlastsignedoroutofdateforrevisionpeibystudentsie -- el MISMO criterio real de
    // "PEI vigente" que ya existía en PeiEngine/PeiRepository (último firmado del año, o el
    // último firmado de un año anterior solo si ya pasó SYS_Parameter.PeiShutdownDate) pero que
    // hasta el 25 ago no estaba conectado a ningún controlador. Antes de esto, Modo de Prueba
    // usaba api/pei/getlastpeibystudentsie (el registro MÁS RECIENTE sin importar su estatus),
    // lo que hacía que probara contra un PEI distinto al que en verdad usan las pantallas reales
    // del PEI -- Astianax lo detectó comparando el PeiId real vs el que resolvía esta búsqueda.
    // No se reutiliza el componente grande de búsqueda de estudiante (StudentSearch/Fast) porque
    // ese está fuertemente acoplado al estado global de navegación de toda la app (selecciona el
    // estudiante a nivel global, dispara tracking, abre la vista completa del estudiante) --
    // efectos secundarios que no tienen sentido dentro de este modal de prueba.
    async buscarEstudianteParaPrueba() {
      this.testSearchError = '';
      this.testPeiLoadError = '';
      this.testStudent = null;
      this.testPeiId = '';
      this.testResult = null;
      this.testError = '';

      const sie = (this.testSieId || '').trim();
      if (!sie) {
        this.testSearchError = 'Escribe un número de SIE.';
        return;
      }

      this.testSearchLoading = true;
      try {
        // Paso 1: resolver el estudiante por SIE. Un error AQUÍ (SIE inválido, no existe, etc.)
        // es el único caso que debe mostrarse como "no se encontró el estudiante".
        let student;
        try {
          const { data } = await studentService.getBySie(sie);
          student = data;
        } catch (e) {
          this.testSearchError =
            e?.response?.data?.message || e?.response?.data || 'No se encontró el estudiante.';
          return;
        }
        this.testStudent = student;

        // Paso 2: resolver el PEI vigente de ese estudiante. Se separa del try/catch anterior
        // a propósito -- un error de RED/servidor aquí (ej. el endpoint todavía no existe porque
        // el backend no se ha recompilado/reiniciado con el controlador nuevo, o cualquier otra
        // falla real del endpoint) NO significa que el estudiante no se encontró, así que se
        // reporta en testPeiLoadError, no en testSearchError, para no confundir al usuario.
        try {
          const { data: academicYear } = await getCurrentAcademicYear();

          const { data: pei } = await http.get(
            'api/pei/getlastsignedoroutofdateforrevisionpeibystudentsie',
            {
              params: {
                studentSieId: student.studentId,
                academicYearId: academicYear.id,
              },
            }
          );

          // null es un resultado válido de este endpoint (no un error): significa que el
          // estudiante todavía no tiene ningún PEI FIRMADO vigente para el año académico actual
          // (ej. solo tiene un borrador sin firmar y aún no pasó la fecha de cierre de revisión)
          // -- ver el comentario del método real en PeiRepository.
          if (!pei || !pei.peiId) {
            this.testPeiLoadError =
              'El estudiante no tiene un PEI firmado vigente para el año académico actual.';
            return;
          }

          this.testPeiId = pei.peiId;
        } catch (e) {
          this.testPeiLoadError =
            e?.response?.data?.message ||
            e?.response?.data ||
            'El estudiante se encontró, pero no se pudo consultar su PEI vigente (revisa que el backend tenga el endpoint más reciente).';
        }
      } finally {
        this.testSearchLoading = false;
      }
    },
    // Envía EXACTAMENTE lo que está en this.model en este momento (guardado o no) -- solo
    // sourceMeta.aiPromptConfigurationId viaja como ancla de auditoría, nunca como fuente del
    // contenido probado. Ver PeiAITestPromptRequest.AIPromptConfigurationId.
    async ejecutarPrueba() {
      if (!this.canTest || !this.testPeiId) {
        return;
      }

      this.testRunning = true;
      this.testError = '';
      this.testResult = null;

      const payload = {
        aiPromptConfigurationId: this.sourceMeta.aiPromptConfigurationId,
        peiId: this.testPeiId,
        systemMessageText: this.model.systemMessageText,
        promptText: this.model.promptText,
        sources: this.model.sources,
        documentSourceOptions: this.model.documentSourceOptions,
        maxTokens: this.model.maxTokens,
        temperature: this.model.temperature,
        topP: this.model.topP,
        responseJsonSchema: this.model.responseJsonSchema || null,
        responseJsonSchemaName: this.model.responseJsonSchemaName || null,
        enumSourcesJson: this.model.enumSourcesJson || null,
      };

      try {
        const { data } = await testPrompt(payload);
        this.testResult = data;
      } catch (e) {
        this.testError = e?.response?.data || e.message;
      } finally {
        this.testRunning = false;
      }
    },
    limpiarPrueba() {
      this.testSieId = '';
      this.testStudent = null;
      this.testPeiId = '';
      this.testSearchError = '';
      this.testPeiLoadError = '';
      this.testResult = null;
      this.testError = '';
      this.testMissingSourceNote = '';
    },
    // ---- Insights de Feedback ----
    // Métricas reales de uso de ESTA versión guardada (LOG_AiGeneration/LOG_AiGenerationFeedback/
    // LOG_AiGenerationAppliedField) -- mismo requisito que canTest (necesita un
    // aiPromptConfigurationId ya guardado), por eso se llama justo después de que sourceMeta
    // queda resuelto, tanto al cargar la pantalla como al crear un borrador nuevo.
    async loadRegulation() {
      if (!this.sourceMeta || !this.sourceMeta.aiPromptConfigurationId) {
        this.regulation = [];
        return;
      }

      this.regulationLoading = true;
      this.regulationError = '';
      try {
        this.regulation = await getRegulation(this.sourceMeta.aiPromptConfigurationId);
      } catch (e) {
        this.regulationError = e?.response?.data || e.message;
      } finally {
        this.regulationLoading = false;
      }
    },
    async refreshRegulation() {
      if (!this.sourceMeta || !this.sourceMeta.aiPromptConfigurationId) return;

      this.regulationRefreshing = true;
      this.regulationError = '';
      try {
        this.regulation = await refreshRegulation(this.sourceMeta.aiPromptConfigurationId);
        this.ShowToast('Proceso Exitoso.', 'Fundamento Normativo actualizado.', 'success');
      } catch (e) {
        this.ShowToast('Error', e?.response?.data || e.message, 'error');
      } finally {
        this.regulationRefreshing = false;
      }
    },
    regulationStatusText(item) {
      if (item.found) return 'Con fuente';
      return item.isError ? 'Error técnico' : 'Sin fuente';
    },
    regulationStatusClass(item) {
      if (item.found) return 'aip-regulation-status--found';
      return item.isError ? 'aip-regulation-status--error' : 'aip-regulation-status--missing';
    },
    async loadInsights() {
      if (!this.sourceMeta || !this.sourceMeta.aiPromptConfigurationId) {
        this.insights = null;
        return;
      }

      this.insightsLoading = true;
      this.insightsError = '';
      try {
        this.insights = await getInsights(this.sourceMeta.aiPromptConfigurationId);
      } catch (e) {
        this.insightsError = e?.response?.data || e.message;
      } finally {
        this.insightsLoading = false;
      }
    },
  },
  async mounted() {
    await this.loadSource();
    await this.loadInsights();
    await this.loadRegulation();
  },
};
</script>

<style scoped>
/*
  Paleta tomada del mockup entregado (24 ago 2026). <style scoped> limita todo esto a lo que
  renderiza este componente (vía el atributo data-v-xxxx que Vue agrega), así que no afecta
  ninguna otra pantalla de MiPE -- tal como se pidió ("solo para este módulo").
*/
.aip-hero-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #ff6b57;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex: 0 0 auto;
}

.aip-hero-title {
  font-weight: 600;
  font-size: 15px;
  color: #17332c;
}

.aip-hero-subtitle {
  font-size: 12.5px;
  color: #6b6b6b;
}

.aip-status-badge {
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 3px 10px;
  border-radius: 999px;
}

.aip-badge-draft {
  background: #eef0e4;
  color: #6b6f4a;
}

.aip-badge-published {
  background: #e3f3ea;
  color: #1f7a4d;
}

.aip-badge-archived {
  background: #f0f0f0;
  color: #6b6b6b;
}

.aip-badge-default {
  background: #eee;
  color: #666;
}

/*
  Restyle de los dos botones de acción para acercarse al mockup (Guardar Borrador -> outline
  verde-carbón oscuro; Guardar y Publicar -> verde más apagado que el verde saturado que usa
  MiPE en el resto de la app). ActionButton pasa "variant" tal cual a una clase Bootstrap
  estándar (btn-outline-success / btn-success), así que se pisan esas clases estándar con
  ::v-deep -- sin depender de ningún detalle interno no verificado de ActionButton.
*/
.aip-btn-draft ::v-deep .btn-outline-success {
  color: #17332c;
  border-color: #17332c;
}
.aip-btn-draft ::v-deep .btn-outline-success:hover {
  background-color: #17332c;
  border-color: #17332c;
  color: #fff;
}

.aip-btn-publish ::v-deep .btn-success {
  background-color: #5c8f76;
  border-color: #5c8f76;
}
.aip-btn-publish ::v-deep .btn-success:hover {
  background-color: #4d7a64;
  border-color: #4d7a64;
}

.aip-meta-card {
  background: #f5f6f7;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
}

.aip-meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
}

.aip-meta-item i {
  color: #ff6b57;
  font-size: 12px;
}

.aip-meta-label {
  color: #6b6b6b;
}

.aip-meta-value {
  color: #17332c;
  font-weight: 600;
}

.aip-draft-banner {
  background: #eef0e4;
  color: #57603c;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 12.5px;
  line-height: 1.5;
}

/* ---- Modo de Prueba (25 ago 2026) -- misma paleta que el resto de la pantalla ---- */
.aip-testmode-card {
  background: #f7f8f9;
  border: 1px solid #ececec;
  border-radius: 10px;
  overflow: hidden;
}

.aip-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
}

.aip-card-header i.fa-flask {
  color: #ff6b57;
}

.aip-card-title {
  font-weight: 600;
  font-size: 13.5px;
  color: #17332c;
}

.aip-card-body {
  padding: 0 16px 16px;
}

.aip-testmode-hint {
  font-size: 12px;
  color: #6b6b6b;
  margin-bottom: 12px;
}

.aip-testmode-locked {
  background: #eef0e4;
  color: #57603c;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 12.5px;
}

.aip-field-label {
  font-size: 12px;
  font-weight: 600;
  color: #17332c;
  display: block;
  margin-bottom: 4px;
}

.aip-field-optional {
  font-weight: 400;
  color: #9ba3ad;
  font-size: 11px;
}

.aip-btn-search,
.aip-btn-run {
  background: #5c8f76;
  border: 1px solid #5c8f76;
  color: #fff;
  border-radius: 6px;
  padding: 6px 16px;
  font-size: 13px;
}

.aip-btn-search:hover,
.aip-btn-run:hover {
  background: #4d7a64;
  border-color: #4d7a64;
}

.aip-btn-search:disabled,
.aip-btn-run:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.aip-btn-clear {
  background: #fff;
  border: 1px solid #ececec;
  color: #17332c;
  border-radius: 6px;
  padding: 6px 16px;
  font-size: 13px;
}

.aip-btn-clear:hover {
  border-color: #17332c;
}

.aip-testmode-error {
  background: #fdecea;
  color: #c0392b;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12.5px;
}

.aip-testmode-student {
  background: #eef7f2;
  color: #17332c;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12.5px;
}

.aip-testmode-pei {
  color: #5c8f76;
  font-weight: 600;
}

.aip-testmode-blocked {
  background: #fdf3e7;
  color: #8a5a10;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 12.5px;
}

.aip-testmode-result-block {
  margin-bottom: 12px;
}

.aip-testmode-result-label {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b6b6b;
  margin-bottom: 4px;
}

.aip-testmode-pre {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12.5px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 320px;
  overflow-y: auto;
  color: #17332c;
  margin: 0;
}

.aip-testmode-context-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 12.5px;
}

.aip-testmode-context-list li {
  padding: 4px 0;
  border-bottom: 1px solid #ececec;
}

.aip-testmode-context-missing {
  color: #9ba3ad;
  font-style: italic;
}

.aip-testmode-log-id {
  font-size: 11.5px;
  color: #9ba3ad;
}

/* ---- Fundamento Normativo ---- */
.aip-regulation-status {
  flex-shrink: 0;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.aip-regulation-status--found {
  background: #e3f1ea;
  color: #1f6b4a;
}

.aip-regulation-status--missing {
  background: #eef0e4;
  color: #57603c;
}

.aip-regulation-status--error {
  background: #fbe7e6;
  color: #a33a33;
}

.aip-regulation-value {
  margin-top: 4px;
  white-space: pre-wrap;
  word-break: break-word;
}

/* ---- Insights de Feedback -- misma paleta que Modo de Prueba ---- */
.aip-insights-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
  margin-bottom: 12px;
}

.aip-insights-stat {
  background: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  padding: 10px 14px;
  min-width: 100px;
  text-align: center;
}

.aip-insights-stat-value {
  font: bold 20px/1.2 Lato;
  color: #17332c;
}

.aip-insights-stat-label {
  font-size: 11px;
  color: #6b6b6b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 2px;
}

.aip-insights-stat--positive .aip-insights-stat-value {
  color: #2db782;
}

.aip-insights-stat--negative .aip-insights-stat-value {
  color: #c0392b;
}

.aip-insights-sample {
  border-top: 1px dashed #e2e2e2;
  padding-top: 10px;
}
</style>
